import type { ThemeConfig } from 'antd';
import type { ThemeDefinition } from './themes/types';
import { koodookaDark } from './themes/koodooka-dark';
import { koodookaLight } from './themes/koodooka';

/**
 * Ant Design configuration derived from the active theme definition.
 * Component states are kept here so Ant components do not fall back to the
 * former static blue and warm-surface palette.
 */
export function createThemeConfig(themeDefinition: ThemeDefinition): ThemeConfig {
    const { tokens } = themeDefinition;
    const isDark = themeDefinition.mode === 'dark';
    const primaryHover = tokens.colorPrimaryHover ?? tokens.colorAccent;
    const primaryActive = tokens.colorPrimaryActive ?? tokens.colorPrimary;
    const primaryBg = tokens.colorPrimaryBg ?? tokens.colorBgBase;
    const inverseText = tokens.colorTextInverse ?? (isDark ? tokens.colorBgBase : tokens.colorBgContainer);

    return {
        token: {
            colorPrimary: tokens.colorPrimary,
            colorPrimaryHover: primaryHover,
            colorPrimaryActive: primaryActive,
            colorPrimaryBg: primaryBg,
            colorPrimaryBgHover: primaryBg,
            colorPrimaryBorder: primaryBg,
            colorPrimaryBorderHover: primaryHover,
            colorLink: tokens.colorPrimary,
            colorLinkHover: primaryHover,
            colorLinkActive: primaryActive,
            colorSuccess: tokens.colorSuccess,
            colorWarning: tokens.colorWarning,
            colorError: tokens.colorError,
            colorInfo: tokens.colorInfo,
            colorText: tokens.colorText,
            colorTextSecondary: tokens.colorTextSecondary,
            colorTextTertiary: tokens.colorTextTertiary,
            colorTextQuaternary: tokens.colorTextTertiary,
            colorTextDescription: tokens.colorTextSecondary,
            colorTextDisabled: tokens.colorTextTertiary,
            colorTextPlaceholder: tokens.colorTextTertiary,
            colorBgBase: tokens.colorBgBase,
            colorBgContainer: tokens.colorBgContainer,
            colorBgElevated: tokens.colorBgElevated,
            colorBgLayout: tokens.colorBgBase,
            colorBgSpotlight: primaryBg,
            colorFill: isDark ? 'rgba(250, 248, 245, 0.08)' : 'rgba(11, 11, 12, 0.05)',
            colorFillSecondary: isDark ? 'rgba(250, 248, 245, 0.06)' : 'rgba(11, 11, 12, 0.03)',
            colorFillTertiary: isDark ? 'rgba(250, 248, 245, 0.04)' : 'rgba(11, 11, 12, 0.02)',
            colorFillQuaternary: isDark ? 'rgba(250, 248, 245, 0.02)' : 'rgba(11, 11, 12, 0.01)',
            colorBorder: tokens.colorBorder,
            colorBorderSecondary: tokens.colorBorderSecondary,
            colorSplit: isDark ? 'rgba(250, 248, 245, 0.10)' : 'rgba(11, 11, 12, 0.08)',
            colorIcon: tokens.colorTextSecondary,
            colorIconHover: tokens.colorPrimary,
            fontFamily: tokens.fontFamily,
            fontSize: tokens.fontSize,
            borderRadius: tokens.borderRadius,
            borderRadiusLG: tokens.borderRadiusLG,
            ...themeDefinition.antdOverrides,
        },
        components: {
            Button: {
                colorPrimary: tokens.colorPrimary,
                colorPrimaryHover: primaryHover,
                colorPrimaryActive: primaryActive,
                primaryColor: inverseText,
                defaultBg: tokens.colorBgContainer,
                defaultBorderColor: tokens.colorBorder,
                defaultColor: tokens.colorText,
                borderRadius: tokens.borderRadius,
            },
            Card: {
                colorBgContainer: tokens.colorBgContainer,
                colorBorderSecondary: tokens.cardBorder,
                boxShadowTertiary: tokens.cardShadow,
                borderRadiusLG: tokens.borderRadiusLG,
            },
            Drawer: {
                colorBgElevated: tokens.colorBgElevated,
            },
            Input: {
                colorBgContainer: tokens.colorBgContainer,
                colorBorder: tokens.colorBorder,
                hoverBorderColor: primaryHover,
                activeBorderColor: tokens.colorFocus ?? tokens.colorPrimary,
                activeShadow: `0 0 0 2px ${primaryBg}`,
                colorTextPlaceholder: tokens.colorTextTertiary,
            },
            Menu: {
                itemBg: 'transparent',
                itemSelectedBg: primaryBg,
                itemSelectedColor: tokens.colorPrimary,
                itemHoverBg: primaryBg,
                itemHoverColor: tokens.colorPrimary,
                itemActiveBg: primaryBg,
            },
            Modal: {
                contentBg: tokens.colorBgElevated,
                headerBg: tokens.colorBgElevated,
                footerBg: tokens.colorBgElevated,
            },
            Select: {
                colorBgContainer: tokens.colorBgContainer,
                colorBorder: tokens.colorBorder,
                optionSelectedBg: primaryBg,
                optionActiveBg: primaryBg,
            },
            Table: {
                colorBgContainer: tokens.colorBgContainer,
                headerBg: tokens.colorBgBase,
                headerColor: tokens.colorText,
                rowHoverBg: primaryBg,
                borderColor: tokens.colorBorder,
            },
            Tabs: {
                inkBarColor: tokens.colorPrimary,
                itemSelectedColor: tokens.colorPrimary,
                itemHoverColor: primaryHover,
                itemActiveColor: primaryActive,
            },
            Tag: {
                defaultBg: primaryBg,
                defaultColor: tokens.colorText,
            },
            Tooltip: {
                colorBgSpotlight: tokens.colorBgSidebar,
                colorTextLightSolid: inverseText,
            },
            Descriptions: {
                labelBg: tokens.colorBgBase,
            },
        },
    };
}

export const lightTheme: ThemeConfig = createThemeConfig(koodookaLight);
export const darkTheme: ThemeConfig = createThemeConfig(koodookaDark);
